import { RouterRootState } from 'connected-react-router';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import DeviceCard from '../../../components/hardware/DeviceCard';
import { DeviceDetails } from '../../../components/hardware/DeviceDetails';
import DeviceActions from '../../../components/hardware/deviceActions';
import DeviceEventActions from '../../../components/hardware/deviceEventActions';
import DeviceInputs from '../../../components/hardware/deviceInputs';
import DeviceParameters from '../../../components/hardware/deviceParameters';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { LOCKS_SYS_NAMES } from '../../../constants/lock-constants';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListQuery } from '../../../models/ListQuery';
import { LockModel } from '../../../models/Lock';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { SimpleItemProfile } from '../../../models/SimpleItemProfile';
import { TabSummary } from '../../../models/TabSummary';
import { getDeviceProfileNames } from '../../../services/hardwarePresetService';
import { getLockById, getLocksNamesWithPagination, storeLock } from '../../../services/lockService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';
import { PRESET_TYPES } from '../../../constants/preset-constants';
import { getDeviceInputTabCount } from '../../../services/deviceInputService';
import { updateTabBadgeOfImproxDevices } from '../../../utilities/tabUtils';
import { toast } from 'react-toastify';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware';
const itemPath = '/access-control/hardware/lock/';

const Lock = () => {
    const { translate } = useLocale();
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value)
    const [itemId, setItemId] = useState(pageId);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();

    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.eventActions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.inputs), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.actions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.parameters), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];

    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();

    const updateBadgeCount = async () => {
        try {
            if(itemId) {
                const deviceInputTabCount = await getDeviceInputTabCount(itemId);                
                setTabs(updateTabBadgeOfImproxDevices(tabs, deviceInputTabCount)); // need to make sure that we update the latest tab change
            }            
        }
        catch (error) {
            showError(error, toast.warn);
        }
    }
    
    const tabContentList: ContentWrapper[] = [
        {
            content: <DeviceDetails />
        },
        {
            content: <DeviceEventActions itemId={itemId} showProvidedSelectOptions={true} />,
        },
        {
            content: <DeviceInputs itemId={itemId}/>,
        },
        {
            content: <DeviceActions itemId={itemId} />,
        },
        {
            content: <DeviceParameters itemId={itemId} />,
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ]

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];

        return selectedTabContent.content;
    };

    const topic: Topic = {
        accessor: 'devName',
        title: translate.byKey(TranslationKeys.locks),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const reloadPage = async () => {

        try {
            let lock;

            if (itemId) {
                setBusy(true);
                lock = await getLockById(itemId);
                setSelectedItem({ label: lock.name, value: lock.id.toString() })
                setCurrentItem(lock);

                setBreadcrumb([
                    { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                    { value: itemPath + `${itemId}`, displayName: `${lock?.name ?? ''}` }
                ]);

                updateBadgeCount();
            }
        } catch (error) {
            const onClick = () => {

                history.push(allItemsPath);
            }
            showLoadItemError(error, dialog, translate, onClick);
        }
        finally {
            // Will need to call this when updating the Preset of the EL-LOCK
            updateBadgeCount();
            setBusy(false);
        }
    }

    const editLock = (values) => {
        const newLock: LockModel = {
            ...currentItem,
            description: values.description,
            name: values.name,
            id: values.id,
            enabled: values.enabled,
            selectedPresetId: values.presetId,
            defaultModeId: values.defaultModeId,
            threatLevelId: values.threatLevelId,
            functionId: values.functionId,
            biometricInput: values.biometricInput,
            musteringPoint: values.musteringPoint,
        }

        setBusy(true);
        storeLock(newLock, false)
            .then(async (value: any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.lockSaved)); 
                reloadPage();
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    const getSysname = (lock) => {
        return lock.profileSysNameType;
    }

    return <TopicContextProvider
        itemId={itemId}
        dataMethod={getLocksNamesWithPagination}
        topic={topic}
        selectedItem={selectedItem} >
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={editLock}
                tabs={tabs}
                initialValues={currentItem}>

                {currentItem && <DeviceCard
                    type='LOCK'
                    dataMethod={(listParameters?: ListQuery) => getDeviceProfileNames(PRESET_TYPES.LOCK,
                        {
                            ...listParameters, filters:
                                appendFilter(siteId, 'Site id', `site.id`,
                                    appendFilter(LOCKS_SYS_NAMES[getSysname(currentItem)], 'Sys name', `profileSysnameType`, listParameters?.filters ?? []))
                        })
                    }
                />}
            </SimpleItemDetails>
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default Lock;